import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import axios from 'axios';
import {
  ArrowLeft,
  Heart,
  Share2,
  ChevronRight,
  Utensils,
  MapPinOff,
  AlertCircle,
} from 'lucide-react';
import { API_URL } from '../utils/api';
import { usePwaNav } from '../contexts/PwaNavContext';
import { track } from '../analytics';
import { EmptyState } from '../components/ui/EmptyState';
import { Button } from '../components/ui/Button';
import VenueRestaurantFeatured from '../components/venue/VenueRestaurantFeatured';
import VenueRestaurantTile from '../components/venue/VenueRestaurantTile';
import './VenueLandingPage.css';

/**
 * Direction A — Editorial / Magazine.
 *
 * Visual language:
 *  - Full-bleed cinematic hero with venue cover photo + Fraunces italic name
 *  - Featured restaurants get full-width 16:10 editorial cards (cover-dominated, tagline, rating)
 *  - Non-featured restaurants render as a 2-col square-cover grid
 *  - Closed restaurants stay in-grid but dimmed (no separate "closed strip")
 *  - One amber promo banner sits between hero and featured section
 *
 * The page renders "Loading" / "404" / "Error" states using existing EmptyState + Button.
 */

interface VenueRestaurant {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  coverImageUrl: string | null;
  cuisine: string | null;
  isFeatured: boolean;
  subscriptionTier: 'FREE' | 'PRO' | 'PREMIUM';
  dineInSupported: boolean;
  tableCount: number;
  acceptsPreorders: boolean;
  openNow: boolean;
  opensAt: string | null;
  productsCount: number;
  rating: number | null;
  avgPrepTime: string | null;
  tagline: string | null;
}

interface VenuePublicResponse {
  venue: {
    name: string;
    slug: string;
    logoUrl: string | null;
    coverImageUrl: string | null;
    location: string | null;
    adBannerEnabled: boolean;
    adBannerText: string | null;
    adBannerSub: string | null;
    adBannerImageUrl: string | null;
    adBannerLink: string | null;
  };
  restaurants: VenueRestaurant[];
}

type LoadState =
  | { status: 'loading' }
  | { status: 'ok'; data: VenuePublicResponse }
  | { status: '404' }
  | { status: 'error' };

export default function VenueLandingPage() {
  const { t } = useTranslation();
  const params = useParams<{ venueSlug: string }>();
  const { navData, navigate: pwaNavigate } = usePwaNav();
  const venueSlug = params.venueSlug ?? navData?.venueSlug;
  const goHome = () => pwaNavigate('/');
  const openRestaurant = (slug: string) => pwaNavigate(`/r/${slug}`);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const heroRef = useRef<HTMLElement>(null);
  const [heroOutOfView, setHeroOutOfView] = useState(false);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setHeroOutOfView(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [state.status]);

  useEffect(() => {
    if (!venueSlug) {
      setState({ status: '404' });
      return;
    }
    let src: string | null = null;
    try {
      const sp = new URLSearchParams(window.location.search);
      src = sp.get('src');
      if (src) sessionStorage.setItem('olso_src', src);
      else src = sessionStorage.getItem('olso_src');
    } catch {
      /* sessionStorage may be unavailable */
    }
    track('venue_qr_scan', { venueSlug, source: src || 'direct' });

    let cancelled = false;
    setState({ status: 'loading' });
    axios
      .get<VenuePublicResponse>(`${API_URL}/api/venues/${venueSlug}/public`)
      .then((res) => {
        if (!cancelled) setState({ status: 'ok', data: res.data });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setState({ status: '404' });
        else setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [venueSlug, reloadKey]);

  if (state.status === 'loading') {
    return (
      <main className="vl-page" aria-busy="true">
        <div className="vl-hero vl-skeleton" />
        <div className="vl-content">
          <div className="vl-skeleton vl-skeleton--name" />
          <div className="vl-feat-list">
            <div className="vl-skeleton vl-skeleton--feat" />
          </div>
          <div className="vl-grid">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="vl-skeleton vl-skeleton--tile" />
            ))}
          </div>
        </div>
      </main>
    );
  }

  if (state.status === '404') {
    return (
      <main className="vl-page vl-page--centered">
        <EmptyState
          icon={<MapPinOff aria-hidden="true" />}
          title={t('venueLanding.notFoundTitle')}
          sub={t('venueLanding.notFoundSub')}
          action={
            <Button variant="secondary" onClick={goHome}>
              {t('venueLanding.notFoundCta')}
            </Button>
          }
        />
      </main>
    );
  }

  if (state.status === 'error') {
    return (
      <main className="vl-page vl-page--centered" role="alert">
        <EmptyState
          icon={<AlertCircle aria-hidden="true" />}
          title={t('venueLanding.errorTitle')}
          sub={t('venueLanding.errorSub')}
          action={
            <Button variant="secondary" onClick={() => setReloadKey((k) => k + 1)}>
              {t('venueLanding.errorCta')}
            </Button>
          }
        />
      </main>
    );
  }

  const { venue, restaurants } = state.data;
  const featured = restaurants.filter((r) => r.isFeatured && r.openNow);
  const others = restaurants.filter((r) => !r.isFeatured || !r.openNow);
  const openCount = restaurants.filter((r) => r.openNow).length;

  return (
    <main className="vl-page">
      {/* STICKY MINI BAR */}
      <AnimatePresence>
        {heroOutOfView && (
          <motion.div
            key="vl-sticky-mini"
            className="vl-sticky-mini"
            initial={{ y: '-100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '-100%', opacity: 0 }}
            transition={{ duration: 0.14, ease: [0.4, 0, 0.2, 1] }}
          >
            <button className="vl-sticky-mini-back" type="button" onClick={goHome} aria-label="Back">
              <ArrowLeft size={18} aria-hidden="true" />
            </button>
            <div className="vl-sticky-mini-title">{venue.name}</div>
            <div className="vl-sticky-mini-spacer" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* HERO */}
      <header className="vl-hero" ref={heroRef}>
        {venue.coverImageUrl ? (
          <img className="vl-hero-img" src={venue.coverImageUrl} alt="" />
        ) : (
          <div className="vl-hero-img vl-hero-img--fallback" />
        )}
        <div className="vl-hero-grad" aria-hidden="true" />

        <div className="vl-bar">
          <button
            className="vl-bar-btn"
            type="button"
            aria-label={t('venueLanding.back', { defaultValue: 'Назад' })}
            onClick={goHome}
          >
            <ArrowLeft size={18} aria-hidden="true" />
          </button>
          <div className="vl-bar-actions">
            <button className="vl-bar-btn" type="button" aria-label="Favorite">
              <Heart size={16} aria-hidden="true" />
            </button>
            <button className="vl-bar-btn" type="button" aria-label="Share">
              <Share2 size={15} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="vl-hero-content">
          <div className="vl-hero-eyebrow">
            <span className="vl-live-dot" aria-hidden="true" />
            {t('venueLanding.foodCourt', { defaultValue: 'FOOD COURT' })}
            {openCount > 0 ? ` · ${t('venueLanding.open')}` : ''}
          </div>
          <h1 className="vl-hero-title">{renderEditorialName(venue.name)}</h1>
          <div className="vl-hero-meta">
            {venue.location && <span>{venue.location}</span>}
            {venue.location && <span className="vl-hero-meta-dot" />}
            <span>
              {restaurants.length}{' '}
              {t('venueLanding.restaurantsLabel', { defaultValue: 'ресторанов' })}
            </span>
            <span className="vl-hero-meta-dot" />
            <span>
              {openCount}{' '}
              {t('venueLanding.openCountLabel', { defaultValue: 'открыто' })}
            </span>
          </div>
        </div>
      </header>

      {/* PROMO BANNER */}
      {venue.adBannerEnabled && venue.adBannerText && (
        <a
          className="vl-promo"
          href={venue.adBannerLink ?? '#'}
          onClick={(e) => {
            if (!venue.adBannerLink) e.preventDefault();
            track('venue_promo_click', { venueSlug });
          }}
        >
          <div className="vl-promo-icon" aria-hidden="true">
            15<span>%</span>
          </div>
          <div className="vl-promo-text">
            <div className="vl-promo-title">{venue.adBannerText}</div>
            {venue.adBannerSub && (
              <div className="vl-promo-sub">{venue.adBannerSub}</div>
            )}
          </div>
          <ChevronRight size={16} className="vl-promo-arrow" aria-hidden="true" />
        </a>
      )}

      <div className="vl-content">
        {/* FEATURED */}
        {featured.length > 0 && (
          <>
            <div className="vl-sect">
              <div className="vl-sect-eyebrow">
                {t('venueLanding.featuredEyebrow', {
                  defaultValue: 'Choice of the venue',
                })}
              </div>
              <h2 className="vl-sect-title">
                {renderEditorialPair(
                  t('venueLanding.featured', { defaultValue: 'Featured' }),
                  t('venueLanding.featuredSuffix', { defaultValue: 'рестораны' }),
                )}
              </h2>
            </div>
            <div className="vl-feat-list">
              {featured.map((r, i) => (
                <VenueRestaurantFeatured
                  key={r.id}
                  restaurant={r}
                  onClick={() => openRestaurant(r.slug)}
                  style={{ animationDelay: `${Math.min(i, 3) * 60}ms` }}
                />
              ))}
            </div>
          </>
        )}

        {/* GRID */}
        {others.length > 0 && (
          <>
            <div className="vl-sect">
              <div className="vl-sect-eyebrow">
                {featured.length > 0
                  ? t('venueLanding.allOthersEyebrow', {
                      defaultValue: 'Все остальные',
                    })
                  : t('venueLanding.allRestaurantsEyebrow', {
                      defaultValue: 'Меню фудкорта',
                    })}
              </div>
              <h2 className="vl-sect-title">
                {renderEditorialPair(
                  t('venueLanding.menu', { defaultValue: 'Меню' }),
                  t('venueLanding.menuSuffix', { defaultValue: 'фудкорта' }),
                )}
              </h2>
            </div>
            <ul className="vl-grid" aria-label={t('venueLanding.title')}>
              {others.map((r, i) => (
                <li
                  key={r.id}
                  className="vl-grid-item"
                  style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}
                >
                  <VenueRestaurantTile
                    restaurant={r}
                    onClick={() => openRestaurant(r.slug)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}

        {restaurants.length === 0 && (
          <EmptyState
            icon={<Utensils aria-hidden="true" />}
            title={t('venueLanding.emptyTitle')}
          />
        )}

        <div className="vl-foot">{venue.name} · Powered by OLSO</div>
      </div>
    </main>
  );
}

/**
 * Editorial title rendering — first word in upright bold, rest italic.
 *  "ALMATY MART" → <b>ALMATY</b> MART (line break preserved)
 */
function renderEditorialName(name: string) {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return <b>{parts[0]}</b>;
  return (
    <>
      <b>{parts[0]}</b>
      <br />
      {parts.slice(1).join(' ')}
    </>
  );
}

/**
 * Editorial title pair — left part bold upright, right part italic.
 *  "Featured", "рестораны" → <b>Featured</b> рестораны
 */
function renderEditorialPair(bold: string, italic: string) {
  return (
    <>
      <b>{bold}</b> {italic}
    </>
  );
}
